import { useAuth0 } from "@auth0/auth0-react";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { Shield, CreditCard, DollarSign, Loader2, CheckCircle, XCircle, User, LogOut, LogIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PocPage() {
  const { loginWithRedirect, logout, user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const [stripeLoading, setStripeLoading] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [meData, setMeData] = useState(null);
  const [meLoading, setMeLoading] = useState(false);
  const [error, setError] = useState(null);

  const stripeStatus = searchParams.get("stripe");
  const paypalStatus = searchParams.get("paypal");

  const handleStripe = async () => {
    setStripeLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/payments/stripe/create-checkout-session`, { method: "POST" });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError("Stripe: No checkout URL returned");
      }
    } catch (e) {
      setError(`Stripe error: ${e.message}`);
    } finally {
      setStripeLoading(false);
    }
  };

  const handlePaypal = async () => {
    setPaypalLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/payments/paypal/create-order`, { method: "POST" });
      const data = await res.json();
      if (data.approval_url) {
        window.location.href = data.approval_url;
      } else {
        setError("PayPal: No approval URL returned");
      }
    } catch (e) {
      setError(`PayPal error: ${e.message}`);
    } finally {
      setPaypalLoading(false);
    }
  };

  const handleGetMe = async () => {
    setMeLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMeData(data);
    } catch (e) {
      setError(`/api/me error: ${e.message}`);
    } finally {
      setMeLoading(false);
    }
  };

  return (
    <div className="poc-container">
      <div className="poc-card">
        <div className="poc-header">
          <Shield className="poc-logo" size={32} />
          <h1>e-mina.eu — Phase 0 POC</h1>
          <p className="poc-subtitle">Auth0 + Stripe + PayPal smoke test</p>
        </div>

        {/* Status banners */}
        {stripeStatus === "success" && (
          <div data-testid="stripe-success-banner" className="poc-banner poc-banner-success">
            <CheckCircle size={18} /> Stripe payment completed successfully
          </div>
        )}
        {stripeStatus === "cancel" && (
          <div data-testid="stripe-cancel-banner" className="poc-banner poc-banner-warn">
            <XCircle size={18} /> Stripe payment was cancelled
          </div>
        )}
        {paypalStatus === "success" && (
          <div data-testid="paypal-success-banner" className="poc-banner poc-banner-success">
            <CheckCircle size={18} /> PayPal payment approved
          </div>
        )}
        {paypalStatus === "cancel" && (
          <div data-testid="paypal-cancel-banner" className="poc-banner poc-banner-warn">
            <XCircle size={18} /> PayPal payment was cancelled
          </div>
        )}

        {error && (
          <div data-testid="error-banner" className="poc-banner poc-banner-error">
            <XCircle size={18} /> {error}
          </div>
        )}

        {/* Auth section */}
        <section className="poc-section">
          <h2><User size={20} /> Authentication</h2>
          {isLoading ? (
            <div className="poc-loading"><Loader2 className="spin" size={20} /> Checking auth state...</div>
          ) : isAuthenticated ? (
            <div className="poc-auth-info">
              <div data-testid="user-email" className="poc-user-email">
                Logged in as <strong>{user?.email}</strong>
              </div>
              <div className="poc-btn-row">
                <button data-testid="logout-button" className="poc-btn poc-btn-outline" onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
                  <LogOut size={16} /> Logout
                </button>
                <button data-testid="get-me-button" className="poc-btn poc-btn-secondary" onClick={handleGetMe} disabled={meLoading}>
                  {meLoading ? <Loader2 className="spin" size={16} /> : <User size={16} />}
                  Test /api/me
                </button>
              </div>
              {meData && (
                <pre data-testid="me-data" className="poc-pre">{JSON.stringify(meData, null, 2)}</pre>
              )}
            </div>
          ) : (
            <button data-testid="login-button" className="poc-btn poc-btn-primary" onClick={() => loginWithRedirect()}>
              <LogIn size={16} /> Login with Auth0
            </button>
          )}
        </section>

        {/* Payment section */}
        <section className="poc-section">
          <h2><CreditCard size={20} /> Payments</h2>
          <div className="poc-btn-row">
            <button data-testid="stripe-button" className="poc-btn poc-btn-stripe" onClick={handleStripe} disabled={stripeLoading}>
              {stripeLoading ? <Loader2 className="spin" size={16} /> : <CreditCard size={16} />}
              Pay $1 with Stripe
            </button>
            <button data-testid="paypal-button" className="poc-btn poc-btn-paypal" onClick={handlePaypal} disabled={paypalLoading}>
              {paypalLoading ? <Loader2 className="spin" size={16} /> : <DollarSign size={16} />}
              Pay $1 with PayPal
            </button>
          </div>
        </section>

        <footer className="poc-footer">
          Phase 0 — Integration smoke test only. Not a production app.
        </footer>
      </div>
    </div>
  );
}
